import { MINIMUM_TOTAL_PAISE, TAX_PERCENT_MAX } from "./PurchaseConstants";
import {
  PRICE_CHECK_STATUS_ENUM,
  TAX_COMPONENT_ENUM,
} from "./PurchaseEnumerations";

export interface ITaxRatesInput {
  cgst: string | number | null;
  sgst: string | number | null;
  igst: string | number | null;
}

interface ITaxBreakdown {
  base_paise: number;
  cgst_paise: number;
  sgst_paise: number;
  igst_paise: number;
  total_paise: number;
}

interface IExactTax {
  label: TAX_COMPONENT_ENUM;
  rate: string;
  exact: string;
  decimals: number;
}

export interface INearestPrice {
  base_paise: number;
  breakdown: ITaxBreakdown;
}

export type TPriceCheck =
  | { status: PRICE_CHECK_STATUS_ENUM.Empty }
  | { status: PRICE_CHECK_STATUS_ENUM.InvalidAmount }
  | { status: PRICE_CHECK_STATUS_ENUM.InvalidRates }
  | {
      status: PRICE_CHECK_STATUS_ENUM.Valid;
      breakdown: ITaxBreakdown;
      below_minimum: boolean;
    }
  | {
      status: PRICE_CHECK_STATUS_ENUM.Inexact;
      exact_taxes: IExactTax[];
      first_inexact: IExactTax;
      below: INearestPrice | null;
      above: INearestPrice;
    };

const BASIS_POINT_SCALE = 10000;
const EXACT_DIVISOR = 1000000;
const AMOUNT_REGEX = /^\d+(\.\d{1,2})?$/;

const greatestCommonDivisor = (a: number, b: number): number =>
  b === 0 ? a : greatestCommonDivisor(b, a % b);

const toHundredths = (value: string | number | null): number | null => {
  if (value === null) return null;
  const text = String(value).trim();
  if (!AMOUNT_REGEX.test(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
};

const groupIndianDigits = (digits: string): string => {
  if (digits.length <= 3) return digits;
  const lastThree = digits.slice(-3);
  const rest = digits.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return `${rest},${lastThree}`;
};

const formatExactRupees = (scaled: number): string => {
  const whole = Math.floor(scaled / EXACT_DIVISOR);
  const fraction = String(scaled % EXACT_DIVISOR)
    .padStart(6, "0")
    .replace(/0+$/, "")
    .padEnd(2, "0");
  return `${groupIndianDigits(String(whole))}.${fraction}`;
};

const toRateText = (basisPoints: number): string => String(basisPoints / 100);

const taxPaise = (basePaise: number, basisPoints: number): number | null => {
  const scaled = basePaise * basisPoints;
  return scaled % BASIS_POINT_SCALE === 0 ? scaled / BASIS_POINT_SCALE : null;
};

const computeBreakdown = (
  basePaise: number,
  rates: [number, number, number],
): ITaxBreakdown | null => {
  const [cgst, sgst, igst] = rates.map((rate) => taxPaise(basePaise, rate));
  if (cgst === null || sgst === null || igst === null) return null;
  return {
    base_paise: basePaise,
    cgst_paise: cgst,
    sgst_paise: sgst,
    igst_paise: igst,
    total_paise: basePaise + cgst + sgst,
  };
};

const validPriceStep = (rates: readonly number[]): number =>
  rates.reduce((step, rate) => {
    const rateStep =
      BASIS_POINT_SCALE / greatestCommonDivisor(rate, BASIS_POINT_SCALE);
    return (step * rateStep) / greatestCommonDivisor(step, rateStep);
  }, 1);

export const formatRupees = (
  value: string | number | null | undefined,
): string => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "0.00";
  const [whole, fraction] = Math.abs(amount).toFixed(2).split(".");
  return `${amount < 0 ? "-" : ""}${groupIndianDigits(whole)}.${fraction}`;
};

export const formatPaise = (paise: number): string => formatRupees(paise / 100);

export const checkPrice = (
  basePrice: string | number | null,
  rates: ITaxRatesInput,
): TPriceCheck => {
  const isEmpty = (value: string | number | null) =>
    value === null || String(value).trim() === "";
  if ([basePrice, rates.cgst, rates.sgst, rates.igst].some(isEmpty)) {
    return { status: PRICE_CHECK_STATUS_ENUM.Empty };
  }

  const basePaise = toHundredths(basePrice);
  if (basePaise === null)
    return { status: PRICE_CHECK_STATUS_ENUM.InvalidAmount };

  const cgst = toHundredths(rates.cgst);
  const sgst = toHundredths(rates.sgst);
  const igst = toHundredths(rates.igst);
  if (
    cgst === null ||
    sgst === null ||
    igst === null ||
    cgst !== sgst ||
    cgst + sgst !== igst ||
    igst > TAX_PERCENT_MAX * 100
  ) {
    return { status: PRICE_CHECK_STATUS_ENUM.InvalidRates };
  }

  const rateList: [number, number, number] = [cgst, sgst, igst];
  const breakdown = computeBreakdown(basePaise, rateList);
  if (breakdown) {
    return {
      status: PRICE_CHECK_STATUS_ENUM.Valid,
      breakdown,
      below_minimum: breakdown.total_paise < MINIMUM_TOTAL_PAISE,
    };
  }

  const labels = [
    TAX_COMPONENT_ENUM.Cgst,
    TAX_COMPONENT_ENUM.Sgst,
    TAX_COMPONENT_ENUM.Igst,
  ];
  const exactTaxes = rateList.map((rate, index) => {
    const scaled = basePaise * rate;
    const exact = formatExactRupees(scaled);
    return {
      label: labels[index],
      rate: toRateText(rate),
      exact,
      decimals: exact.split(".")[1].length,
    };
  });
  const firstInexact =
    exactTaxes.find((tax) => tax.decimals > 2) ?? exactTaxes[0];

  const step = validPriceStep(rateList);
  const belowPaise = Math.floor(basePaise / step) * step;
  const abovePaise = Math.ceil(basePaise / step) * step;
  const belowBreakdown =
    belowPaise > 0 ? computeBreakdown(belowPaise, rateList) : null;
  const abovePrice = abovePaise === basePaise ? abovePaise + step : abovePaise;
  const aboveBreakdown = computeBreakdown(abovePrice, rateList);
  if (!aboveBreakdown) return { status: PRICE_CHECK_STATUS_ENUM.InvalidRates };

  return {
    status: PRICE_CHECK_STATUS_ENUM.Inexact,
    exact_taxes: exactTaxes,
    first_inexact: firstInexact,
    below:
      belowBreakdown && belowBreakdown.total_paise >= MINIMUM_TOTAL_PAISE
        ? { base_paise: belowPaise, breakdown: belowBreakdown }
        : null,
    above: { base_paise: abovePrice, breakdown: aboveBreakdown },
  };
};

export const isPriceBlocked = (check: TPriceCheck, allowZeroTotal: boolean) =>
  check.status === PRICE_CHECK_STATUS_ENUM.Inexact ||
  check.status === PRICE_CHECK_STATUS_ENUM.InvalidRates ||
  (check.status === PRICE_CHECK_STATUS_ENUM.Valid &&
    check.below_minimum &&
    !allowZeroTotal);
